import { buildRoleByS3Url } from "~/entities/chat";
import { findModeConfig, getModelById } from "~/shared/data/chat-models";
import { toGenerationMode } from "~/shared/lib/generation-mode";
import { makeLocalId } from "~/shared/lib/local-id";
import { fileNameFromKey } from "~/shared/lib/uploads/file-name";

import type { ChatAttachment, ChatMessage, LegacyGenerationJob } from "~/entities/chat";
import type { GenerationMode, ImageInputRole, ModeConfig, ModelConfig } from "~/shared/data/chat-models";
import type { PendingUpload, PromptPrefill } from "~/shared/store/types";

// `requestPayload` типизирован как открытый словарь (`[key: string]: unknown`) —
// это снимок запроса, который бэк пишет в JSONB. Читаем его узкими guard'ами, а
// не кастами: у исторических генераций поля может не быть вовсе.
const asString = (value: unknown): string | undefined => (typeof value === "string" ? value : undefined);
const asNumber = (value: unknown): number | undefined =>
    typeof value === "number" && Number.isFinite(value) ? value : undefined;

/** Референсные картинки снимка; у исторических генераций поля нет вовсе. */
const referenceImageUrls = (payload: LegacyGenerationJob["requestPayload"]): string[] =>
    Array.isArray(payload.referenceImageUrls)
        ? payload.referenceImageUrls.filter((url): url is string => typeof url === "string")
        : [];

/**
 * Вложение сообщения → готовый `PendingUpload` (status done): кадры повторяем
 * теми же s3-ключами, файл не перезаливается. Отдельно от
 * `pick-attachment/selectionToPendingUploads`: там источник — item пикера без
 * `sha256`, а здесь хэш есть и его нужно донести до реестра референсов, плюс
 * назначается роль кадра.
 */
function attachmentToPending(
    attachment: ChatAttachment,
    role: ImageInputRole | undefined,
    channel?: "reference",
): PendingUpload {
    return {
        localId: makeLocalId("repeat", attachment.s3Key),
        fileName: fileNameFromKey(attachment.s3Key),
        kind: attachment.kind,
        mimeType: attachment.mimeType,
        sizeBytes: attachment.sizeBytes,
        status: "done",
        progress: 100,
        s3Key: attachment.s3Key,
        previewUrl: attachment.s3Url,
        ...(attachment.sha256 !== null ? { sha256: attachment.sha256 } : {}),
        ...(role ? { role } : {}),
        ...(channel ? { channel } : {}),
    };
}

function buildAttachments(
    message: ChatMessage,
    payload: LegacyGenerationJob["requestPayload"],
    mode: ModeConfig,
) {
    const uploaded = message.attachments.filter((a) => a.status === "uploaded");
    const roleByUrl = buildRoleByS3Url(payload.inputImageUrls, mode.imageRoles);

    // Референс прошлого запроса возвращается в СВОЮ колонку, а не кадром: кадром
    // провайдер сделал бы из него первый/последний, а лишние отбросил молча —
    // человек нажал бы «Сгенерировать», не увидев подмены, и заплатил за другой
    // запрос. Режим мог с тех пор ужаться, поэтому и здесь берём столько, сколько
    // канал принимает сейчас; что не влезло — посчитано как отброшенное.
    const referenceUrls = new Set(referenceImageUrls(payload));
    const uploadedImages = uploaded.filter((a) => a.kind === "image");
    // Кадрового канала у режима может не быть вовсе (мультимодальный Seedance:
    // нативно все картинки там — референсы). Снимки прошлых запросов держат их в
    // кадровом поле, и кадром такую картинку теперь не положить — повтор пришёл
    // бы без единого визуального входа. Её место — колонка референсов, тот же
    // канал, которым запрос и исполнялся.
    const framesClosed = mode.maxInputImages === 0 && (mode.maxReferenceImages ?? 0) > 0;
    const wasReference = (a: ChatAttachment) => referenceUrls.has(a.s3Url);
    const images = framesClosed ? [] : uploadedImages.filter((a) => !wasReference(a));
    // Порядок как у исполнителя: свой канал впереди исторического кадрового —
    // при переполнении отбрасывается то, что референсом изначально не было.
    const references = framesClosed
        ? [...uploadedImages.filter(wasReference), ...uploadedImages.filter((a) => !wasReference(a))]
        : uploadedImages.filter(wasReference);
    const limitedReferences = references.slice(0, mode.maxReferenceImages ?? 0);
    // Режим мог с тех пор ужаться (меньше слотов) — берём столько кадров,
    // сколько он принимает сейчас, чтобы бар не оказался переполненным.
    const limited = mode.maxInputImages === null ? images : images.slice(0, mode.maxInputImages);
    const others = uploaded.filter((a) => a.kind !== "image");

    return {
        attachments: [
            ...limited.map((a) => attachmentToPending(a, roleByUrl.get(a.s3Url))),
            ...limitedReferences.map((a) => attachmentToPending(a, undefined, "reference")),
            ...others.map((a) => attachmentToPending(a, undefined)),
        ],
        // Отброшены только те референсы, которым не нашлось места: либо канала у
        // режима больше нет, либо лимит стал меньше. Ноль — повтор полон. При
        // закрытом кадровом канале претендентами становятся все картинки снимка,
        // поэтому и считаются они все.
        droppedReferenceImages: Math.max(
            (framesClosed ? references.length : referenceUrls.size) - limitedReferences.length,
            0,
        ),
    };
}

/**
 * Снимок настроек генерации из payload'а — отдельно от сборки заявки, чтобы
 * чтение необязательных полей не смешивалось с проверками «повторимо ли».
 * Отсутствующие в снимке поля не подставляются вовсе: `applyPreset` — merge,
 * и `undefined` затёр бы текущее значение стора нулём.
 */
function buildSettings(
    payload: LegacyGenerationJob["requestPayload"],
    model: ModelConfig,
    mode: GenerationMode,
): PromptPrefill["settings"] {
    const aspectRatio = asString(payload.aspectRatio);
    const resolution = asNumber(payload.resolution);
    const duration = asNumber(payload.videoDurationSeconds);
    const characterOrientation = asString(payload.characterOrientation);
    const upscaleFactor = asNumber(payload.upscaleFactor);
    const batchSize = asNumber(payload.batchSize);

    return {
        // Порядок применения (модель+режим раньше кадров) держит `useRepeatPrefill`;
        // здесь это один объект — `applyPreset` кладёт его атомарно.
        artifactType: model.artifactType,
        modelId: model.id,
        mode,
        ...(aspectRatio ? { aspectRatio } : {}),
        ...(resolution !== undefined ? { resolution } : {}),
        // Повтор пакета обязан просить столько же результатов: снимок хранит
        // размер именно для этого. Без него повтор пакета из четырёх молча
        // возвращал одну картинку.
        ...(batchSize !== undefined ? { batchSize } : {}),
        duration: duration ?? 0,
        audioEnabled: payload.withSound === true,
        characterOrientation: characterOrientation ?? null,
        // Повтор апскейла обязан повторить кратность: без неё ×4 уезжал как ×2
        // (первая ступень режима) — другой результат и другая цена.
        upscaleFactor: upscaleFactor ?? null,
    };
}

/** Вход сборщика: сообщение с вложениями, снимок его генерации и справочник моделей. */
export interface BuildRepeatPrefillParams {
    message: ChatMessage;
    job: LegacyGenerationJob;
    /** `undefined` — справочник ещё грузится: повторить нечем, вернётся `null`. */
    models: readonly ModelConfig[] | null | undefined;
}

/**
 * Собирает заявку на повтор запроса из сообщения и снимка его генерации.
 *
 * Возвращает `null`, когда повторить нечего или нечем: у джобы нет режима, либо
 * модель/режим больше не доступны на платформе. Молча подставлять «похожий»
 * запрос в таком случае нельзя — человек нажал бы «Сгенерировать», не увидев
 * подмены, поэтому действие в UI просто не показывается.
 */
export function buildRepeatPrefill({ message, job, models }: BuildRepeatPrefillParams): PromptPrefill | null {
    const payload = job.requestPayload;
    const backendMode = asString(payload.generationMode);
    if (!backendMode) return null;

    const model = getModelById(models, job.modelName);
    if (!model) return null;

    const mode = toGenerationMode(backendMode);
    const modeConfig = findModeConfig(model, mode);
    if (!modeConfig) return null;

    const elementIds = (payload.referenceElements ?? [])
        .map((element) => element.elementId)
        .filter((id): id is string => typeof id === "string" && id.length > 0)
        .slice(0, modeConfig.maxElements ?? 0);

    const { attachments, droppedReferenceImages } = buildAttachments(message, payload, modeConfig);

    // Промпт остаётся в хранимом виде (ссылки по id) — имена в него подставит
    // промпт-бар, у которого под рукой актуальная библиотека. Снимок имён несём
    // рядом: он запасной на случай, когда библиотека ещё не загружена.
    return {
        prompt: message.prompt,
        settings: buildSettings(payload, model, mode),
        attachments,
        elementIds,
        elementNames: (payload.referenceElements ?? [])
            .filter((element): element is typeof element & { elementId: string } => !!element.elementId)
            .map((element) => ({ id: element.elementId, name: element.name })),
        droppedReferenceImages,
    };
}
